import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  ArrowLeft,
  Mail,
  Pencil,
  Phone,
  User,
  Briefcase,
  MapPin,
  Calendar,
  Users,
  Clock,
  IndianRupee,
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import { Button } from "../../components/ui/button";
import { ThrowError } from "../../api/callApi";
import { RoutesLink } from "../../utility/RouteLinks";
import { WorkerService } from "@/services/Labor/worker";

// ─── Interface matches the exact API response ─────────────────────
interface ILaborDetail {
  id: string;
  workerId: string;
  fullName: string;
  fatherName?: string;
  phoneNumber: string;
  aadharNumber?: string;
  siteId: string;
  shiftId: string;
  checkInBufferTime?: string;
  checkOutBufferTime?: string;
  dailyRate: number | string; // API returns string "650.00"
  joinDate: string;
  status: "active" | "inactive";
  contactPerson?: string;
  cpNumber?: string;
  gender: "male" | "female" | "other";
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null; // ✅ allows undefined or null
  Site?: {
    id: string;
    aliasId: string;
    siteName: string;
    siteCode: string;
  };
  Shift?: {
    id: string;
    aliasId: string;
    shiftName: string;
    startTime: string;
    endTime: string;
  };
}

// ─── Reusable DetailField ─────────────────────────────────────────
const DetailField = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number | undefined;
}) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0 mt-0.5">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">
        {value ?? "—"}
      </p>
    </div>
  </div>
);

// ─── Section wrapper ──────────────────────────────────────────────
const DetailSection = ({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="border-b border-gray-100 last:border-b-0 px-6 py-7 sm:px-8">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-[13px] text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);

// ─── Main Component ──────────────────────────────────────────────
const LaborDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [labor, setLabor] = useState<ILaborDetail | null>(null);

  useEffect(() => {
    const fetchLaborDetail = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await WorkerService.getWorkerDetail(id);
        if (!response?.success || !response.data) {
          throw new Error("Failed to load worker");
        }
        setLabor(response.data); // ✅ now matches ILaborDetail
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLaborDetail();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        Loading labor details...
      </div>
    );
  }

  if (!labor) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-muted-foreground">Labor record not found.</p>
        <Button variant="outline" onClick={() => navigate(RoutesLink.labor)}>
          Back to Labor List
        </Button>
      </div>
    );
  }

  const initials = labor.fullName
    .split(" ")
    .map((p) => p.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const genderDisplay = labor.gender
    ? labor.gender.charAt(0).toUpperCase() + labor.gender.slice(1)
    : "—";

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 w-9 p-0 hover:bg-muted shrink-0"
            onClick={() => navigate(RoutesLink.labor)}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <MainHeading title="Labor Details" />
            <p className="text-[13px] text-gray-400 -mt-0.5">
              Complete profile and assignment
            </p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        {/* Profile header */}
        <div className="flex items-center gap-4 px-6 py-5 sm:px-8 border-b border-gray-100">
          <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold text-gray-900 truncate">
              {labor.fullName}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              <span className="text-xs text-gray-400">{labor.workerId}</span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full ${
                  labor.status === "active"
                    ? "text-green-600 border-green-600 bg-green-50"
                    : "text-red-600 border-red-600 bg-red-50"
                }`}
              >
                {labor.status.charAt(0).toUpperCase() + labor.status.slice(1)}
              </span>
              {labor.phoneNumber && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone size={12} /> {labor.phoneNumber}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ─── Personal Information ──────────────────────────── */}
        <DetailSection
          icon={<User size={17} />}
          title="Personal Information"
          description="Identity and contact details"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <DetailField
              icon={<User size={15} />}
              label="Employee Code"
              value={labor.workerId}
            />
            <DetailField
              icon={<User size={15} />}
              label="Full Name"
              value={labor.fullName}
            />
            <DetailField
              icon={<User size={15} />}
              label="Father's Name"
              value={labor.fatherName}
            />
            <DetailField
              icon={<User size={15} />}
              label="Gender"
              value={genderDisplay}
            />
            <DetailField
              icon={<Phone size={15} />}
              label="Phone Number"
              value={labor.phoneNumber}
            />
            <DetailField
              icon={<User size={15} />}
              label="Aadhar Number"
              value={labor.aadharNumber}
            />
          </div>
        </DetailSection>

        {/* ─── Work Details ───────────────────────────────────── */}
        <DetailSection
          icon={<Briefcase size={17} />}
          title="Work Details"
          description="Site, shift, and compensation"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <DetailField
              icon={<MapPin size={15} />}
              label="Site"
              value={labor.Site?.siteName}
            />
            <DetailField
              icon={<Clock size={15} />}
              label="Shift"
              value={labor.Shift?.shiftName}
            />
            <DetailField
              icon={<Clock size={15} />}
              label="Check-in Buffer"
              value={labor.checkInBufferTime?.slice(0, 5)}
            />
            <DetailField
              icon={<Clock size={15} />}
              label="Check-out Buffer"
              value={labor.checkOutBufferTime?.slice(0, 5)}
            />
            <DetailField
              icon={<Calendar size={15} />}
              label="Joining Date"
              value={moment(labor.joinDate).format("DD-MM-YYYY")}
            />
            <DetailField
              icon={<IndianRupee size={15} />}
              label="Daily Rate"
              value={`₹${labor.dailyRate}`}
            />
          </div>
        </DetailSection>

        {/* ─── Emergency Contact ─────────────────────────────── */}
        <DetailSection
          icon={<Users size={17} />}
          title="Emergency Contact"
          description="Who to reach in case of an incident"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <DetailField
              icon={<Users size={15} />}
              label="Contact Person"
              value={labor.contactPerson}
            />
            <DetailField
              icon={<Phone size={15} />}
              label="Contact Number"
              value={labor.cpNumber}
            />
          </div>
        </DetailSection>
      </div>
    </div>
  );
};

export default LaborDetail;
